// File: src/redux/RTK/productsApi.ts

import { createApi } from "@reduxjs/toolkit/query/react";
import { createEntityAdapter, EntityState } from "@reduxjs/toolkit";
import type { AxiosError } from "axios";
import { ProductDetail, CreditDetail, Review } from "../types/ProductDetailTypes";
import { Product } from "../types/ProductsTypes";
import { axiosBaseQuery } from "../axiosBaseQuery"; // <-- import custom base query
import axiosGetToken from "../auth/axiosGetToken";

/**
 * 1) Create an entity adapter with string ID.
 *    - We specify <Product, string> to indicate Product is the entity,
 *      and the ID type is string.
 */
const productsAdapter = createEntityAdapter<Product, string>({
  /**
   * We tell the adapter how to extract the entity's unique ID.
   * In your data, the ID is `_id`.
   */
  selectId: (product) => product._id
});

type ProductsEntityState = EntityState<Product, string>;

export const productsApi = createApi({
  reducerPath: "productsApi",

  // Instead of fetchBaseQuery, use our custom axiosBaseQuery
  baseQuery: axiosBaseQuery({ baseUrl: "/v1/product" }),

  tagTypes: ["Products", "ProductDetail", "CreditDetail", "Reviews"],
  endpoints: (builder) => ({
    // -------------------------------------
    // 1) Fetch multiple products -> entityAdapter usage
    // -------------------------------------
    fetchProducts: builder.query<
      ProductsEntityState,
      { searchQuery?: string; filters?: Record<string, string> }
    >({
      query: ({ searchQuery, filters } = {}) => {
        let queryString = "";

        if (filters) {
          const filterParams = Object.entries(filters)
            .filter(([, value]) => value)
            .map(
              ([key, value]) =>
                `${encodeURIComponent(key)}=${encodeURIComponent(value)}`,
            )
            .join("&");
          if (filterParams) {
            queryString += filterParams;
          }
        }

        if (searchQuery) {
          if (queryString.length > 0) {
            queryString += `&search=${encodeURIComponent(searchQuery)}`;
          } else {
            queryString = `search=${encodeURIComponent(searchQuery)}`;
          }
        }

        const url = queryString ? `/products?${queryString}` : "/products";
        return { url, method: "GET" };
      },
      /**
       * Transform the array response into an EntityState<Product, string>
       * by using our adapter.
       */
      transformResponse: (responseData: Product[]) => {
        return productsAdapter.setAll(
          productsAdapter.getInitialState(),
          responseData,
        );
      },
      providesTags: (result) =>
        result
          ? [
              ...result.ids.map((id) => ({ type: "Products" as const, id })),
              { type: "Products", id: "LIST" },
            ]
          : [{ type: "Products", id: "LIST" }],
    }),

    // -------------------------------------
    // 2) Fetch product by ID
    // -------------------------------------
    fetchProductById: builder.query<Product, string>({
      query: (id) => ({ url: `/id/${id}`, method: "GET" }),
      providesTags: (result, error, id) => [{ type: "Products", id }],
    }),

    fetchSuggestedProducts: builder.query<Product[], string>({
      query: (id) => ({ url: `/suggested/${id}`, method: "GET" }),
      providesTags: (result, error, id) => [{ type: "Products", id }],
    }),

    // -------------------------------------
    // 3) Fetch product by ID & platform
    // -------------------------------------
    fetchProductByIdAndPlatform: builder.query<
      Product,
      { productId: string; platformId: string; lang: string }
    >({
      query: ({ productId, platformId, lang }) => ({
        url: `/id/${productId}/${platformId}?lang=${lang}`,
        method: "GET",
      }),
      providesTags: (result, error, { productId }) => [
        { type: "Products", id: productId },
      ],
    }),

    // -------------------------------------
    // 4) Fetch product with title
    // -------------------------------------
    fetchProductWithTitle: builder.query<Product, string>({
      query: (slug) => ({
        url: `/title/${slug}`,
        method: "GET",
      }),
      providesTags: (result, error, slug) => [{ type: "Products", id: slug }],
    }),

    // -------------------------------------
    // 4) Fetch product detail by ID
    // -------------------------------------
    fetchProductDetailById: builder.query<
      ProductDetail,
      { productId: string; lang: string }
    >({
      query: ({ productId, lang }) => ({
        url: `/product-details/${productId}?lang=${lang}`,
        method: "GET",
      }),
      providesTags: (result, error, arg) => [
        { type: "ProductDetail", id: arg.productId },
      ],
    }),

    // -------------------------------------
    // 5) Fetch credit detail by ID
    // -------------------------------------
    fetchCreditDetailById: builder.query<
      CreditDetail,
      { productId: string; lang: string }
    >({
      query: ({ productId, lang }) => ({
        url: `/credit-details/${productId}?lang=${lang}`,
        method: "GET",
      }),
      providesTags: (result, error, arg) => [
        { type: "CreditDetail", id: arg.productId },
      ],
    }),

    // -------------------------------------
    // 6) Fetch product screenshots
    // -------------------------------------
    fetchProductScreenshots: builder.query<{ images: string[] }, string>({
      async queryFn(productId) {
        const CDN_BASE = "https://bucket.bigswiss.ch";
        const normalizeToCdn = (url: string) => {
          if (!url) return url;
          try {
            const parsed = new URL(url);
            return `${CDN_BASE}${parsed.pathname}`;
          } catch {
            const safePath = url.startsWith("/")
              ? url
              : `/${url}`;
            return `${CDN_BASE}${safePath}`;
          }
        };
        if (!productId) {
          return { data: { images: [] } };
        }
        try {
          const response = await axiosGetToken.get<{ images: string[] }>(
            `/v1/product/screenshots`,
            { params: { productId } },
          );
          const normalized = Array.isArray(response.data?.images)
            ? response.data.images.map(normalizeToCdn)
            : [];
          return { data: { images: normalized } };
        } catch (error) {
          const err = error as AxiosError;
          return {
            error: {
              status: err.response?.status ?? "FETCH_ERROR",
              data: err.response?.data ?? err.message,
            },
          };
        }
      },
    }),

    // -------------------------------------
    // 7) Fetch reviews by product ID
    // -------------------------------------
    fetchReviewsByProductId: builder.query<Review[], string>({
      query: (productId) => ({
        url: `/reviews/${productId}`,
        method: "GET",
      }),
      providesTags: (result, error, productId) => [
        { type: "Reviews", id: productId },
      ],
    }),

      // -------------------------------------
      // 8) Post a new review
      // -------------------------------------
      postReview: builder.mutation<
        Review,
        { productId: string; user: string; rating: number; comment?: string }
      >({
        query: (payload) => ({
          url: `/reviews`,
          method: "POST",
          data: payload,
        }),
        invalidatesTags: (result, error, { productId }) => [
          { type: "Reviews", id: productId },
        ],
      }),

      
  }),
});

// ------------------------------------------------
// Hooks for each endpoint (auto-generated by RTK)
// ------------------------------------------------
export const {
  useFetchProductsQuery,
  useFetchProductByIdQuery,
  useFetchProductWithTitleQuery,
  useFetchProductByIdAndPlatformQuery,
  useFetchProductDetailByIdQuery,
  useFetchCreditDetailByIdQuery,
  useFetchProductScreenshotsQuery,
  useFetchSuggestedProductsQuery,
  useFetchReviewsByProductIdQuery,
  usePostReviewMutation,
} = productsApi;
